import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { getSessionData } from '../reducer';
import './session-log-page.scss';
import { sessionLogColumns } from '../constants';
import { Icons } from '../../../components/base/BootstrapComponents';
import {
  loadSessionList, loadSessionId, loadSessionStatusList, loadSessionIdClear,
} from '../actions';
import {
  getPaginationOpts, copyText, getJsonFromStorage, useUpdatePeriod,
} from '../../../utils/utils';
import TextInput from '../../../components/fields/TextInput/TextInput';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import PageHeader from '../../../components/base/PageHeader/PageHeader';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import SessionLogGrid from './SessionLogGrid';
import SessionLogFilterForm from './SessionLogFilterForm';
import IconBox from '../../../components/common/IconBox';
import SeparateBox from '../../../components/base/SeparateBox/SeparateBox';
import SessionLogStatus from './SessionLogStatus';
import Loader from '../../../components/base/Loader/Loader';
import { getSystemData } from '../../System/reducer';
import SessionModalClose from './SessionModalClose';
import { sessionStatusFormatter } from '../../../utils/formatters';
import ModuleUnavailableState from '../../../components/base/ModuleUnavailableState';
import { isMenuUnavailable } from '../../System/serviceAvailability';

const sessionTypeOptions = [
  { name: 'Разовая', value: 'regular' },
  { name: 'Льготная', value: 'preferential' },
  { name: 'Абонемент', value: 'subscription' },
  { name: 'Белый список', value: 'white_list' },
];

export default function SessionLogPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const { updatePeriod, serviceAvailability } = useSelector(getSystemData);
  const {
    sessionList,
    sessionListMeta,
    sessionStatusList,
    session,
    loadingSessionId,
  } = useSelector(getSessionData);
  const [searchParams, setSearchParams] = useSearchParams();
  const session_id = searchParams.get('session_id');
  const isModuleUnavailable = isMenuUnavailable(serviceAvailability, 'menu.session-log');

  const handlerClickRow = item => {
    setSearchParams({ session_id: item.id });
  };

  const clearParams = () => {
    const { pathname } = window.location;
    navigate(pathname, { replace: true });
  };

  const sessionStatusOptions = sessionStatusList.map(type => ({ nameId: type.label, value: type.value }));

  const defaultFilterValues = {
    search: '',
    status: '',
    type: '',
    started_at_from: '',
    started_at_to: '',
    ended_at_from: '',
    ended_at_to: '',
  };

  const savedFilter = getJsonFromStorage('slp-filter', {});
  const initial = {
    ...defaultFilterValues,
    search: savedFilter?.search || '',
    status: savedFilter?.status || '',
    type: savedFilter?.type || '',
    started_at_from: savedFilter?.started_at_from || '',
    started_at_to: savedFilter?.started_at_to || '',
    ended_at_from: savedFilter?.ended_at_from || '',
    ended_at_to: savedFilter?.ended_at_to || '',
  };

  const {
    control, getValues, watch, setValue, reset,
  } = useForm({ defaultValues: initial });

  const loadData = opts => {
    if (isModuleUnavailable) {
      return Promise.resolve([]);
    }

    const filterData = getValues();
    const paginationOpts = getPaginationOpts(sessionListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: filterData,
    };
    const p1 = dispatch(loadSessionList(requestData)).catch(() => {});
    return Promise.all([p1]);
  };

  const [setPeriod, destroy] = useUpdatePeriod(loadData, updatePeriod);

  const clear = () => {
    dispatch(loadSessionIdClear());
    clearParams();
  };

  useEffect(() => {
    setPeriod(updatePeriod);
    return destroy;
  }, [updatePeriod]);

  useEffect(() => {
    const subscription = watch(values => {
      loadData();
      localStorage.setItem('slp-filter', JSON.stringify(values));
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (isModuleUnavailable || !session_id) {
      return;
    }

    dispatch(loadSessionId({ id: session_id })).catch(() => {});
  }, [session_id, searchParams, isModuleUnavailable]);

  useEffect(() => {
    if (isModuleUnavailable) {
      return;
    }

    dispatch(loadSessionStatusList()).catch(() => {});
  }, [isModuleUnavailable]);

  return (
    <div className="session-log-page">
      <Loader loading={loadingSessionId} />
      <div className="slp-container">
        <div className="slp-inputs-box">
          <div className={classNames('slp-title-page', { variant: session?.id })}>
            {
              session?.id ? (
                <div className="path">
                  <span className="path-main" onClick={clear}>Журнал сессий</span> / <span>Статус сессии</span>
                </div>
              ) : null
            }
            <PageHeader header={session?.id ? `Статус сессии ${session.id}` : 'Журнал сессий'} />
          </div>
          {
            !session?.id ? (
              <div className="slp-inputs">
                <FieldDTO
                  inputBefore={<Icons customIcon className="search-icon" width={24} height={24} icon="search" />}
                  type="text"
                  control={control}
                  component={TextInput}
                  dtoObject={{
                    name: 'search',
                    type: String,
                    validators: [],
                    labelKey: 'search',
                  }}
                  placeholder="Поиск"
                  className="small no-border search-field "
                />
                <FieldDTO
                  type="text"
                  control={control}
                  dtoObject={{
                    name: 'type',
                    type: String,
                    validators: [],
                    labelKey: 'slp.sessionType',
                  }}
                  component={CustomSelectInput}
                  addPlaceholder
                  options={sessionTypeOptions}
                  className="select-field no-border"
                />
                <FieldDTO
                  type="text"
                  control={control}
                  dtoObject={{
                    name: 'status',
                    type: String,
                    validators: [],
                    labelKey: 'slp.sessionStatus',
                  }}
                  component={CustomSelectInput}
                  renderOption={sessionStatusFormatter}
                  addPlaceholder
                  options={sessionStatusOptions}
                  className="select-field no-border"
                />
                <SeparateBox />
                <IconBox icon="filter" onClick={() => { setIsOpenFilter(true); }} />
                {isOpenFilter
                  ? (
                    <SessionLogFilterForm
                      onHide={() => { setIsOpenFilter(false); }}
                      setValue={setValue}
                      getValues={getValues}
                      sessionMeta={sessionListMeta}
                      reset={reset}
                      control={control}
                      defaultValues={defaultFilterValues}
                      sessionStatusOptions={sessionStatusOptions}
                      sessionTypeOptions={sessionTypeOptions}
                    />
                  )
                  : null}
              </div>
            )
              : (
                <div className="slp-inputs">
                  <IconBox icon="document" onClick={() => copyText(session, sessionLogColumns)} />
                </div>
              )
          }
        </div>
        {isModuleUnavailable ? <ModuleUnavailableState /> : null}
        {!isModuleUnavailable && !session?.id ? <SessionLogGrid setValue={setValue} setShowCloseModal={setShowCloseModal} data={sessionList} meta={sessionListMeta} loadData={loadData} onClickByRow={handlerClickRow} /> : null}
        {!isModuleUnavailable && session?.id ? <SessionLogStatus /> : null}
        {!isModuleUnavailable && showCloseModal ? <SessionModalClose loadData={loadData} showCloseModal={showCloseModal} setShowCloseModal={setShowCloseModal} /> : null}
      </div>
    </div>
  );
}
